import { html } from '@acme/b2b/utils';
import { CartService, CartServiceModule } from '@b2b/common/services';
import { AuthenticationService, AuthenticationServiceModule } from '@b2b/common/services/auth/authentication.service';
import { getFullName } from '@b2b/common/services/helpers';
import { ThemesHelperService } from '@b2b/common/services/themesHelper';
import { TODO } from '@b2b/types/core';
import { OrderContact } from '@b2b/types/order';
import { Organization } from '@b2b/types/organization';
import uiRouter, { StateService } from '@uirouter/angularjs';
import angular, { ITimeoutService, growl } from 'angular';
import { CartDirectiveModule } from '../cart/cart.directive';

/**
 * `<header-cart>` — the cart trigger + popover, carved out of the legacy
 * `header` directive so the surrounding header chrome can migrate to Angular
 * (`app-header`) while the cart stays AngularJS. It is projected into
 * `app-header`'s `<ng-content>` and compiled by AngularJS (ngUpgrade compiles
 * downgraded-component projected content with `$compile`), so `uib-popover`,
 * the `data-cart` directive and the `<ng-spinner>` keep working unchanged.
 *
 * The controller is exposed as `header` on the isolate scope so the existing
 * `cart.tpl.html` popover template (which references `header.*`) needs no edits.
 *
 * Holds only the cart-coupled state from the old header directive. Dropped
 * here as dead (no live emitter): `header::message` and `AppController::cart::toggle`.
 */
HeaderCartDirective.$inject = [
  '$state',
  '$timeout',
  'growl',
  'AuthenticationService',
  'CartService',
  'FeatureToggleService',
  'ThemesHelperService',
];

function HeaderCartDirective(
  $state: StateService,
  $timeout: ITimeoutService,
  growl: growl.IGrowlService,
  authenticationService: AuthenticationService,
  CartService: CartService,
  FeatureToggleService: TODO,
  ThemesHelperService: ThemesHelperService,
) {
  HeaderCartController.$inject = ['$scope'];

  function HeaderCartController($scope) {
    let user = authenticationService.getUserFromCookie();

    const ctrl = {
      cartHint: 'View Cart',
      viewCartHint: 'View Cart',
      emptyCartHint: 'Cart is empty',

      user: user,
      isLoggedIn: user && (user.hasOwnProperty('email') || user.hasOwnProperty('userName')),
      isCartOpen: false,
      cartItemCount: 0,
      cartInitted: false,
      cartService: CartService,
      hideSpinner: false,
      spinnerTimer: null as angular.IPromise<void> | null,
      cartInitTimer: null as angular.IPromise<void> | null,
      useCart: false,
      orderContact: {} as Partial<OrderContact> | null,
      organization: {} as Partial<Organization> | null,
      growlCartMessage: {} as growl.IGrowlMessage,

      init: function () {
        ctrl.useCart = FeatureToggleService.check('TENANT_CART');

        $scope.$on('AppController::logout::success', ctrl.onLogoutSuccess);
        $scope.$on('AppController::cart::open', ctrl.onCartOpen);
        $scope.$on('AppController::cart::close', ctrl.onCartClose);
        $scope.$on('$destroy', $scope.$watch('header.cartService.itemCount', ctrl.onCartServiceItemCountUpdate));
        $scope.$on('$destroy', $scope.$watch('header.cartService.events.events.length', ctrl.onCartEventsChanged));
        $scope.$on('$destroy', $scope.$watch('header.cartService.initted', ctrl.onCartServiceInitted));
        $scope.$on(
          '$destroy',
          $scope.$watchCollection('header.cartService.orderContact', ctrl.onCartServiceOrderContactUpdated),
        );
        $scope.$on(
          '$destroy',
          $scope.$watchCollection('header.cartService.organization', ctrl.onCartServiceOrganizationUpdate),
        );
        $scope.$on('$destroy', ctrl.killTimers);
      },

      killTimers: function () {
        if (ctrl.spinnerTimer) {
          $timeout.cancel(ctrl.spinnerTimer);
        }
        if (ctrl.cartInitTimer) {
          $timeout.cancel(ctrl.cartInitTimer);
        }
      },

      updateCartHint: function (hint) {
        ctrl.cartHint = ctrl.cartService.itemCount || ctrl.cartService.events.events.length ? hint : ctrl.emptyCartHint;
      },

      launchCart: function (e) {
        e.preventDefault();
        e.stopPropagation();

        if (ctrl.cartService.itemCount || ctrl.cartService.events.events.length) {
          const organization = ctrl.cartService.getOrganization();

          if (organization && organization.id) {
            ctrl.isCartOpen = true;
          } else {
            $state.go('application.cart');
          }
        }
      },

      getFullName: function (contact) {
        return getFullName(contact);
      },

      getCompanyName: function () {
        return ThemesHelperService.getCompany('name') || 'Tenant ID: ' + ctrl.user?.tenantId;
      },

      logout: function () {
        user = null;
        ctrl.user = user;
        ctrl.isLoggedIn = false;
      },

      onLogoutSuccess: function (e, data) {
        ctrl.logout();
      },

      /**
       * Opens cart and invokes growl notification
       * data parameter requires itemName and assumes
       * a cart item otherwise defaults to 'Item'
       */
      onCartOpen: function (e, data) {
        const name = (data && data.itemName) || 'Item';

        ctrl.showMessage({ message: name + ' added to the cart.', fallback: true });

        ctrl.isCartOpen = true;
      },

      /**
       * Uses JQuery event mechanism instead of angular
       */
      showMessage: function (props: { message?: string; fallback: boolean }) {
        const markup = html`
          <a
            class="header-cart-growl"
            onclick="event.preventDefault();event.stopPropagation();$(document).trigger('Header::Top')"
            class="btn btn-link">
            <span class="fa fa-2x fa-shopping-cart"></span>
            <span>${props.message || ''} </span>
          </a>
        `;

        $(document).off('Header::Top');
        $(document).on('Header::Top', () => {
          ctrl.growlCartMessage.destroy();

          if (props.fallback) {
            // Fallback in case user has clicked away
            ctrl.isCartOpen = true;
          }

          $('html, body').animate({ scrollTop: 0 }, 'fast');
        });

        ctrl.growlCartMessage = growl.success(markup);
      },

      onCartClose: function (e, data) {
        ctrl.isCartOpen = false;
      },

      onCartServiceItemCountUpdate: function (newValue, oldValue) {
        if (newValue) {
          ctrl.cartHint = ctrl.viewCartHint;
        } else {
          ctrl.cartHint = ctrl.emptyCartHint;
        }
      },

      onCartServiceInitted: function (newValue, oldValue) {
        if (newValue) {
          ctrl.killTimers();
          ctrl.cartInitTimer = $timeout(() => {
            ctrl.cartInitted = newValue;
            ctrl.spinnerTimer = $timeout(() => {
              ctrl.hideSpinner = true;
            }, 500);
          }, 1000);
        }
      },

      onCartEventsChanged: function (newValue, oldValue) {
        if (newValue) {
          ctrl.cartHint = ctrl.viewCartHint;
        } else {
          ctrl.cartHint = ctrl.emptyCartHint;
        }
      },

      onCartServiceOrderContactUpdated: function (newValue, oldValue) {
        ctrl.orderContact = ctrl.cartService.getOrderContact();
      },

      onCartServiceOrganizationUpdate: function (newValue, oldValue) {
        if (newValue && newValue.id) {
          ctrl.organization = ctrl.cartService.getOrganization();
        } else {
          ctrl.organization = {};
        }
      },
    };

    $scope.header = ctrl;
    ctrl.init();
  }

  return {
    restrict: 'E',
    templateUrl: 'common/directives/header/header-cart.tpl.html',
    scope: {},
    controller: HeaderCartController,
  };
}

export const HeaderCartDirectiveModule = angular
  .module('acme.common.directive.headerCart', [
    uiRouter,
    'ui.bootstrap',
    'angular-growl',
    AuthenticationServiceModule,
    CartServiceModule,
    'acme.common.service.featureToggle',
    'acme.common.service.helpers',
    CartDirectiveModule,
    'acme.common.service.themesHelper',
  ])
  .directive('headerCart', HeaderCartDirective).name;
